<template>
	<div class="__setup-center px-10 md-down:px-4 pb-30">
		<div class="__setup-center__header pt-10 pb-4">
			<div
				class="__setup-center__title text-neutral-500 font-semibold whitespace-nowrap flex flex-nowrap items-center"
			>
				<DaIcon class="mr-3 h-6 w-6" name="das--gear" />
				Thiết lập cơ sở
			</div>
		</div>
		<div>
			<div class="md:hidden">
				<div class="p-6 bg-white select-tabs">
					<Multiselect
						v-model="selected"
						:allow-empty="false"
						:options="tabsMenu"
						:searchable="true"
						deselect-label="Đã chọn"
						label="nameSearch"
						selectLabel=""
						track-by="id"
						@select="tabSelectEvent"
					>
						<template #noResult> Không tìm thấy kết quả</template>
						<template v-slot:option="props">
							<div class="flex flex-nowrap items-center">
								<div>
									{{ props.option.name }}
								</div>
							</div>
						</template>
						<template v-slot:singleLabel="{ option }">
							<div
								class="flex flex-nowrap items-center font-medium text-yellow-700"
							>
								<DaIcon
									:name="option.icon"
									class="h-3.5 w-3.5 mr-4"
								/>
								<div>
									{{ option.name }}
								</div>
							</div>
						</template>
					</Multiselect>
				</div>
			</div>
			<div class="border-b border-neutral-200 md-down:hidden">
				<nav aria-label="Tabs" class="-mb-px flex space-x-8 tabs">
					<router-link
						v-for="tab in tabsMenu"
						:key="tab.id"
						:to="tab.link"
						class="border-transparent text-neutral-500 hover:text-yellow-700 hover:border-yellow-500 whitespace-nowrap py-3 px-1 border-b-2 font-semibold"
					>
						{{ tab.name }}
					</router-link>
				</nav>
			</div>
			<div class="__setup-center__content h-full w-full mt-10">
				<router-view
					:key="org"
					v-model:updated="updateSuccessful"
					:loading="loading"
					:org="org"
					:student_count="student_count"
					:timetable="timetable"
					@updated="onUpdated"
					@update-org="updateOrg"
				></router-view>
			</div>
		</div>
	</div>
</template>

<script>
import { Options, setup, Vue } from 'vue-class-component';
import DaIcon from '@/components/elements/daIcon/DaIcon.vue';
import { tabs } from '@/views/setupCenter/data/tabs';
import Multiselect from '@suadelabs/vue3-multiselect';
import { useGetOrg } from '@/views/setupCenter/services/getOrg';
import { useUpdateOrg } from '@/views/setupCenter/services/updateOrg';
import { useStore } from 'vuex';
import { $api } from '@/services';
import { _ROUTER_NAME } from '@/enums';
import { isLitePackage } from '@/helper/packageTier';
import { Watch } from 'vue-property-decorator';

@Options({
	name: 'SetupCenter',
	components: { DaIcon, Multiselect },
})
export default class SetupCenter extends Vue {
	//----------------------- 🤍 Data 🤍 -----------------------//
	selected = {};
	options = ['list', 'of', 'options'];
	limit = 10;
	org = null;
	updateSuccessful = false;
	timetable = {};
	student_count = 0;
	loading = false;
	tabsMenu = [];

	//----------------------- 🤍 Setup 🤍 -----------------------//
	setup = setup(() => {
		const store = useStore();
		const { error, loading, getOrg } = useGetOrg();
		const { update_org_error, updateOrg } = useUpdateOrg();
		return { error, update_org_error, loading, getOrg, updateOrg, store };
	});

	//----------------------- 🤍 Getters 🤍 -----------------------//

	get store() {
		return this.setup.store;
	}

	get isLitePackage() {
		return isLitePackage(this.store.state.profile);
	}
	//----------------------- 🤍 Created 🤍 -----------------------//
	created() {
		this.fetchOrg();
		if (!this.isLitePackage) this.fetchTimetableScheduleActive();
	}

	mounted() {
		this.syncTabs();
	}

	@Watch('$route.path')
	onSetupPathChange() {
		this.syncTabs();
	}

	syncTabs() {
		const profile = this.setup.store.state.profile;
		const hiddenOnLite = [
			_ROUTER_NAME.SETUP_SCHEDULE,
			_ROUTER_NAME.SETUP_LUCA_AI,
			_ROUTER_NAME.SETUP_OTHERS,
		];
		const lite = isLitePackage(profile);
		this.tabsMenu = tabs.filter((item) => {
			if (!item.state.includes(profile.center_state)) return false;
			if (lite && hiddenOnLite.includes(item.id)) return false;
			return true;
		});
		if (lite && hiddenOnLite.includes(this.$route.name)) {
			this.selected = this.tabsMenu[0];
			this.$router.replace('/setup-center');
			return;
		}
		const res = this.tabsMenu.find(
			(item) => item.link === this.$route.path
		);
		this.selected = res || this.tabsMenu[0];
	}

	//----------------------- 🤍 Methods 🤍 -----------------------//
	async updateOrg(orgUpdate) {
		try {
			const {
				data: { data },
			} = await $api.org.updateOrg({ org: orgUpdate });
			if (data) {
				this.org = data;
				this.updateSuccessful = true;
				await this.store.dispatch('setAlertMessage', {
					message: 'Cập nhật thông tin thành công',
				});
				// window.location.reload();
			}
		} catch (err) {
			if (err?.response?.data?.errors_by_field['start_opening_hour']) {
				const error =
					'Giờ bắt đầu ' +
					err?.response?.data?.errors_by_field[
						'start_opening_hour'
					].join('<br/>');
				await this.store.dispatch('setAlertMessage', {
					message: error,
					type: 'danger',
				});
			} else if (
				err?.response?.data?.errors_by_field.org_profile[
					'limit_ai_chat_count'
				]
			) {
				const error =
					'Số tin nhắn giới hạn mỗi tháng phải lớn hơn hoặc bằng 10';
				await this.store.dispatch('setAlertMessage', {
					message: error,
					type: 'danger',
				});
			} else {
				const error = err?.response?.data?.errors || err.message;
				await this.store.dispatch('setAlertMessage', {
					message: error.join('<br/>'),
					type: 'danger',
				});
			}
		}
	}

	async fetchTimetableScheduleActive() {
		try {
			this.loading = true;
			const res = await $api.schedule.fetchTimetableScheduleActive();
			if (res.data) {
				this.timetable = res.data.data;
				this.student_count = res.data.student_count;
			}
			this.loading = false;
		} catch (e) {
			console.log(e);
		}
	}

	onUpdated() {
		this.fetchOrg();
	}

	async fetchOrg() {
		const res = await this.setup.getOrg();
		if (res && res?.data) {
			this.org = res?.data;
		}
	}

	tabSelectEvent(selectedOption) {
		this.$router.push(selectedOption.link);
	}
}
</script>
<style lang="scss">
@import 'setup-center';
</style>
